import { useReactiveVar } from "@apollo/client";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { ReportContentViolationEnum } from "@/graphql/mutations/use-report-content";
import { gameUrl } from "@/helpers/games";
import useShare from "@/hooks/use-share";
import { user as userStore } from "@/store";

import type { GameType, ReviewType } from "../types";
import { EditIcon, OffensiveIcon, ShareIcon, SpoilersIcon } from "./icons";
import OptionItem from "./option-item";

interface MenuItemsProps {
  game: GameType;
  onReport(violation: ReportContentViolationEnum): () => void;
  review: ReviewType;
}

export default function MenuItems({ game, onReport, review }: MenuItemsProps) {
  const user = useReactiveVar(userStore);
  const navigate = useNavigate();
  const { onShare } = useShare({
    url: `${process.env.APP_URL}${gameUrl(game)}'/review/${review.id}`,
  });

  const handleEditReview = () => {
    navigate({ to: `/experience/${game.id}/${review.escape.id}` });
  };

  const isMyReview = useMemo(() => {
    if (!user) {
      return false;
    }

    return review.user.id === user.id;
  }, [review, user]);

  return (
    <ul className="flex flex-col items-stretch gap-2.5">
      {isMyReview && (
        <li>
          <OptionItem
            icon={<EditIcon />}
            onClick={handleEditReview}
            title="Edit details"
          />
        </li>
      )}
      <li>
        <OptionItem
          icon={<ShareIcon />}
          onClick={onShare}
          title="Share review"
        />
      </li>
      <li>
        <OptionItem
          icon={<SpoilersIcon />}
          onClick={onReport(ReportContentViolationEnum.SPOILERS)}
          title="Review contains spoilers"
        />
      </li>
      <li>
        <OptionItem
          icon={<OffensiveIcon />}
          onClick={onReport(ReportContentViolationEnum.OFFENSIVE)}
          title="Review is offensive"
        />
      </li>
    </ul>
  );
}
